import { FC } from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SyncIcon from '@mui/icons-material/Sync';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import styled from 'styled-components';
import { ChartRangeMode } from '../../../common/model/type/chart-range-selection.type';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface RangeModeToggleProps {
  mode: ChartRangeMode;
  onChange: (mode: ChartRangeMode) => void;
  customLabel: string;
  cycleLabel: string;
  ariaLabel: string;
  customAriaLabel: string;
  cycleAriaLabel: string;
  cycleDisabled?: boolean;
}

const RangeModeToggle: FC<RangeModeToggleProps> = ({
  mode,
  onChange,
  customLabel,
  cycleLabel,
  ariaLabel,
  customAriaLabel,
  cycleAriaLabel,
  cycleDisabled = false,
}) => (
  <ModeToggleWrapper>
    <StyledToggleButtonGroup
      value={mode}
      exclusive
      onChange={(_, newMode) => {
        if (newMode !== null) {
          onChange(newMode);
        }
      }}
      aria-label={ariaLabel}
    >
      <StyledToggleButton value="custom" aria-label={customAriaLabel}>
        <DateRangeIcon fontSize="small" />
        <span>{customLabel}</span>
      </StyledToggleButton>
      <StyledToggleButton
        value="cycle"
        aria-label={cycleAriaLabel}
        disabled={cycleDisabled}
      >
        <SyncIcon fontSize="small" />
        <span>{cycleLabel}</span>
      </StyledToggleButton>
    </StyledToggleButtonGroup>
  </ModeToggleWrapper>
);

export default RangeModeToggle;

const ModeToggleWrapper = styled.div`
  padding: 0.5rem 1rem;

  @media (max-width: ${breakpoints.md}) {
    padding: 0.5rem 1rem 0;
    min-width: 100%;
  }
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  background-color: ${Colours.OV_WHITE};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  padding: 0.25rem;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;

  @media (max-width: ${breakpoints.md}) {
    min-width: 100%;
  }
`;

const StyledToggleButton = styled(ToggleButton)`
  && {
    color: ${Colours.OV_BASE_LIGHT};
    border: none;
    border-radius: ${Variables.borderRadius.LARGE} !important;
    text-transform: none;
    font-weight: 600;
    padding: 0.375rem 1.25rem;
    line-height: 1.5;
    display: flex;
    gap: 0.5rem;
    transition: all 0.2s ease-in-out;

    &.Mui-selected {
      background-color: ${Colours.OV_BASE};
      color: ${Colours.OV_WHITE};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

      &:hover {
        background-color: ${Colours.OV_BASE};
        opacity: 0.9;
      }
    }

    &:hover:not(.Mui-selected) {
      background-color: ${Colours.OV_LIGHT};
      color: ${Colours.OV_BASE};
    }
  }
`;
